import posthog from 'posthog-js';

// Read shareAnalytics preference from persisted zustand store
const getShareAnalyticsPref = (): boolean => {
  try {
    const raw = localStorage.getItem('settings-storage');
    if (raw) {
      const parsed = JSON.parse(raw);
      return parsed?.state?.settings?.shareAnalytics ?? false;
    }
  } catch { /* ignore */ }
  return false;
};

// Initialize PostHog
export const initPostHog = () => {
  const apiKey = import.meta.env.VITE_POSTHOG_KEY;

  if (apiKey) {
    posthog.init(apiKey, {
      api_host: import.meta.env.VITE_POSTHOG_HOST,
      capture_pageview: true,
      capture_pageleave: true,
      autocapture: true,
      persistence: 'localStorage+cookie',
      respect_dnt: true,
      loaded: (ph) => {
        if (import.meta.env.DEV || !getShareAnalyticsPref()) {
          ph.opt_out_capturing();
        }
      },
    });
  }
};

// Toggle analytics capturing based on user preference
export const setAnalyticsEnabled = (enabled: boolean) => {
  if (import.meta.env.DEV) return;
  if (enabled) {
    posthog.opt_in_capturing();
  } else {
    posthog.opt_out_capturing();
  }
};

// Identify user after login
export const identifyUser = (userId: string, properties?: Record<string, unknown>) => {
  posthog.identify(userId, properties);
};

// Reset on logout
export const resetUser = () => {
  posthog.reset();
};

// Map-specific events - these are the key geospatial metrics
export const trackEvent = {
  // Core map interactions
  mapOpened: () => posthog.capture('map_opened'),
  mapStyleChanged: (style: string) => posthog.capture('map_style_changed', { style }),

  // Search - critical for understanding user intent
  searchPerformed: (query: string, resultCount: number) =>
    posthog.capture('search_performed', {
      query_length: query.length,
      result_count: resultCount,
      has_results: resultCount > 0
    }),
  searchResultSelected: (resultType: string) =>
    posthog.capture('search_result_selected', { result_type: resultType }),

  // Geocoding - key product metric
  addressGeocoded: (success: boolean) =>
    posthog.capture('address_geocoded', { success }),
  reverseGeocoded: (success: boolean) =>
    posthog.capture('reverse_geocoded', { success }),

  // Postcode interactions - unique to your platform
  postcodeViewed: (postcode: string) =>
    posthog.capture('postcode_viewed', { postcode_prefix: postcode.substring(0, 4) }),
  postcodeCopied: () => posthog.capture('postcode_copied'),
  postcodeShared: (method: string) =>
    posthog.capture('postcode_shared', { method }),

  // Navigation/Directions
  directionsRequested: (mode: string) =>
    posthog.capture('directions_requested', { travel_mode: mode }),
  directionsCompleted: (distanceKm: number, durationMin: number) =>
    posthog.capture('directions_completed', {
      distance_km: distanceKm,
      duration_min: durationMin
    }),

  // Place interactions
  placeViewed: (placeType: string) =>
    posthog.capture('place_viewed', { place_type: placeType }),
  placeDetailsExpanded: () => posthog.capture('place_details_expanded'),

  // Contributions
  locationContributed: (type: string) =>
    posthog.capture('location_contributed', { contribution_type: type }),

  // Geolocation
  userLocationRequested: () => posthog.capture('user_location_requested'),
  userLocationGranted: () => posthog.capture('user_location_granted'),
  userLocationDenied: () => posthog.capture('user_location_denied'),

  // Tiles - important for infrastructure costs
  tilesLoaded: (layer: string, count: number) =>
    posthog.capture('tiles_loaded', { layer, tile_count: count }),

  // Errors
  searchError: (errorType: string) =>
    posthog.capture('search_error', { error_type: errorType }),
  rateLimitHit: () => posthog.capture('rate_limit_hit'),
};

export default posthog;
